// The entry shape and its validation. Deliberately knows nothing about where the
// declaration came from, so the source can move (static file today, backend or
// dashboard later) without the trust boundary moving with it.

export type RuntimeRouteKind = "addition" | "override" | "subtree" | "override-subtree";

export type ScriptFormat = "iife" | "module";

export interface RuntimeRouteEntry {
  kind: RuntimeRouteKind;
  path: string;
  elementId: string;
  scriptUrl: string;
  variableName: string;
  // Absent means "iife". Left absent rather than defaulted, so an entry that declares
  // none parses without a scriptFormat key.
  scriptFormat?: ScriptFormat;
}

const REQUIRED_STRING_FIELDS = ["path", "elementId", "scriptUrl", "variableName"] as const;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isScriptFormat(value: unknown): value is ScriptFormat {
  return value === "iife" || value === "module";
}

const LOOPBACK_HOSTNAMES: ReadonlySet<string> = new Set(["localhost", "127.0.0.1"]);

// Entries reach a console.error, so they have to render as text even when they are
// something JSON cannot represent (a cyclic object, a BigInt).
function formatValue(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

// What this buys is transport integrity: an http script URL could be swapped in
// flight, an https one could not. What it does not buy is any assurance about who
// serves the script — every https origin passes, so this is not an allowlist and
// the entry still runs arbitrary JavaScript with full page privileges.
//
// The one exception is a module bundle on the developer's own machine, where there is
// no wire to tamper with. It is read off import.meta.env.DEV at call time, which a
// production build folds to false, so no build that ships ever accepts an http URL.
function isAllowedScriptUrl(value: string, scriptFormat: ScriptFormat | undefined): boolean {
  try {
    const url = new URL(value);

    if (url.protocol === "https:") {
      return true;
    }
    return import.meta.env.DEV && scriptFormat === "module" && url.protocol === "http:" && LOOPBACK_HOSTNAMES.has(url.hostname);
  } catch {
    return false;
  }
}

// A subtree stores the plain base it claims and never a match pattern, so that the entry
// stays a small declarative record any config source can carry unchanged; the pattern that
// reaches every address below the base is derived where routes are assembled.
//
// Which is only worth anything if the base really is a plain address, hence an allowlist
// rather than a ban on ":" and "*". Nothing here crashes — every ASCII special character
// compiles under path-to-regexp — so a ban would read as validation while still accepting a
// base whose derived pattern no URL can reach: "/lobby?x=1" and "/lobby#a" match that one
// literal address and nothing under it, a regex metacharacter matches text nobody typed, and
// a "," survives into the key React renders the route under. Enumerating what an address may
// contain is the only form of this check that cannot be walked around.
const SUBTREE_BASE_CHARACTERS = /^\/[a-z0-9\-_/]*$/;

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

// Reads an already-normalised base, which is what makes the root rejection hold: "/", "//"
// and "///" all normalise to the empty string, and a base of "/" would derive a pattern
// claiming every address in the app.
function isValidSubtreeBase(base: string): boolean {
  return base.length > 0 && SUBTREE_BASE_CHARACTERS.test(base) && !base.includes("//");
}

function toValidEntry(candidate: unknown): RuntimeRouteEntry | undefined {
  if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) {
    console.error(`[runtime-routes] Dropping route entry ${formatValue(candidate)} — expected an object.`);
    return undefined;
  }

  const entry = candidate as Record<string, unknown>;

  if (entry.kind !== "addition" && entry.kind !== "override" && entry.kind !== "subtree" && entry.kind !== "override-subtree") {
    console.error(
      `[runtime-routes] Dropping route entry ${formatValue(entry)} — kind must be "addition", "override", "subtree" or "override-subtree", got ${formatValue(entry.kind)}.`,
    );
    return undefined;
  }

  const missingField = REQUIRED_STRING_FIELDS.find((field) => !isNonEmptyString(entry[field]));
  if (missingField) {
    console.error(
      `[runtime-routes] Dropping route entry ${formatValue(entry)} — ${missingField} must be a non-empty string, got ${formatValue(entry[missingField])}.`,
    );
    return undefined;
  }

  // The trimmed value is kept, not only validated against: a padded scriptUrl stored as
  // written would compare unequal to the tag the DOM builds from it, and the bundle would
  // be evaluated a second time.
  const path = (entry.path as string).trim();
  const elementId = (entry.elementId as string).trim();
  const scriptUrl = (entry.scriptUrl as string).trim();
  const variableName = (entry.variableName as string).trim();

  if (!path.startsWith("/")) {
    console.error(`[runtime-routes] Dropping route entry ${formatValue(entry)} — path must start with "/".`);
    return undefined;
  }

  // The base allowlist applies to the two subtree-shaped kinds: an addition or an override claims
  // one exact address and is free to spell it as a pattern — "/promo/:id" is a legitimate
  // declaration — whereas a subtree and an override-subtree each stand for a whole range below a
  // plain base, and only a plain base derives a pattern every address under it can reach.
  let claimedPath = path;

  if (entry.kind === "subtree" || entry.kind === "override-subtree") {
    claimedPath = stripTrailingSlashes(path);

    if (!isValidSubtreeBase(claimedPath)) {
      console.error(
        `[runtime-routes] Dropping route entry ${formatValue(entry)} — a ${entry.kind} path must be a plain lowercase base such as "/exclusive-lobby", got ${formatValue(path)}.`,
      );
      return undefined;
    }
  }

  // Validated rather than passed through, because a typo would otherwise silently pick
  // the wrong evaluation path. Checked before the URL, which the format decides the rule for.
  const scriptFormat = entry.scriptFormat;

  if (scriptFormat !== undefined && !isScriptFormat(scriptFormat)) {
    console.error(`[runtime-routes] Dropping route entry ${formatValue(entry)} — scriptFormat must be "iife" or "module", got ${formatValue(scriptFormat)}.`);
    return undefined;
  }

  // Dropped rather than downgraded to the IIFE path: a module bundle evaluated as a classic
  // script defines no global and would only fail later, less legibly.
  if (scriptFormat === "module" && !import.meta.env.DEV) {
    console.error(`[runtime-routes] Dropping route entry ${formatValue(entry)} — module scripts are not available in this build.`);
    return undefined;
  }

  if (!isAllowedScriptUrl(scriptUrl, scriptFormat)) {
    console.error(`[runtime-routes] Dropping route entry ${formatValue(entry)} — scriptUrl must be an https URL, or loopback http for a module in dev.`);
    return undefined;
  }

  return { kind: entry.kind, path: claimedPath, elementId, scriptUrl, variableName, ...(scriptFormat !== undefined && { scriptFormat }) };
}

/**
 * Validates a raw runtime route declaration, dropping each entry that does not
 * hold up rather than rejecting the whole payload. A single bad entry must cost
 * one address, not the app.
 *
 * @param raw - The parsed declaration, from container config or a static file
 * @returns Every entry that validated, trimmed, in declaration order
 */
export function parseRuntimeRouteConfig(raw: unknown): RuntimeRouteEntry[] {
  if (!Array.isArray(raw)) {
    console.error(`[runtime-routes] Ignoring route config ${formatValue(raw)} — expected an array of route entries.`);
    return [];
  }

  return raw.map(toValidEntry).filter((entry): entry is RuntimeRouteEntry => entry !== undefined);
}
